// src/lib/PlanContext.js
// OnboardingProvider と同じ思想：判定・状態・モーダル描画を1箇所に集約する。
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { PLAN_LIMITS } from './plans';
import { isDemoMode } from './demoMode';
import UpgradeModal from '../components/UpgradeModal';

const PlanContext = createContext({
  plan: 'free',
  isPro: false,
  isLifetime: false,
  limits: PLAN_LIMITS.free,
  subscription: null,
  loading: true,
  refresh: () => {},
  openUpgrade: () => {},
  requirePro: () => false,
  canAddProject: () => true,
  handlePlanError: () => false,
});

export const usePlan = () => useContext(PlanContext);

export function PlanProvider({ children }) {
  const [plan, setPlan] = useState('free');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrade, setUpgrade] = useState({ open: false, reason: 'default' });
  const [redirecting, setRedirecting] = useState(false);
  const polledRef = useRef(false);

  const isPro = plan === 'pro';
  const isLifetime = !!subscription?.is_lifetime; // 初期ユーザー特典
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;

  // ---- プラン取得 ----
  const refresh = useCallback(async () => {
    if (isDemoMode()) {
      setPlan('pro'); // デモは全機能を見せる
      setLoading(false);
      return 'pro';
    }

    const { data: sess } = await supabase.auth.getSession();
    const user = sess?.session?.user;
    if (!user) {
      setPlan('free');
      setLoading(false);
      return 'free';
    }

    const { data } = await supabase
      .from('subscriptions')
      .select('plan, status, current_period_end, cancel_at_period_end, is_lifetime')
      .eq('user_id', user.id)
      .maybeSingle();

    setSubscription(data || null);

    // ① 初期ユーザー特典（永久Pro）は無条件でPro
    // ② それ以外はStripeの状態で判定
    const active =
      data &&
      (data.is_lifetime ||
        (data.plan === 'pro' &&
          ['active', 'trialing'].includes(data.status) &&
          (!data.current_period_end || new Date(data.current_period_end) > new Date())));

    const next = active ? 'pro' : 'free';
    setPlan(next);
    setLoading(false);
    return next;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ---- 決済直後：Webhookが届くまで数秒ラグがあるのでポーリング ----
  useEffect(() => {
    if (polledRef.current) return;
    if (typeof window === 'undefined') return;
    if (!window.location.search.includes('checkout=success')) return;

    polledRef.current = true;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      const p = await refresh();
      if (p === 'pro' || tries >= 6) clearInterval(timer);
    }, 2000);

    return () => clearInterval(timer);
  }, [refresh]);

  // ---- モーダル ----
  const openUpgrade = useCallback((reason = 'default') => {
    setUpgrade({ open: true, reason });
  }, []);

  // Proが必要な操作の前に呼ぶ。true が返ったら実行してよい。
  const requirePro = useCallback(
    (featureKey) => {
      if (limits[featureKey]) return true;
      openUpgrade(featureKey);
      return false;
    },
    [limits, openUpgrade]
  );

  // 案件追加の事前チェック（UI用。最終防衛はDBトリガー）
  const canAddProject = useCallback(
    (currentCount) => {
      if (currentCount < limits.maxProjects) return true;
      openUpgrade('projects');
      return false;
    },
    [limits, openUpgrade]
  );

  // Supabaseのエラーがプラン制限由来なら拾ってモーダルを出す
  const handlePlanError = useCallback(
    (error) => {
      if (error?.message?.includes('PLAN_LIMIT_PROJECTS')) {
        openUpgrade('projects');
        return true;
      }
      return false;
    },
    [openUpgrade]
  );

  // ---- Stripe ----
  const callApi = async (path) => {
    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    if (!token) throw new Error('ログインが必要です');

    const res = await fetch(path, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.url) throw new Error(json.error || 'failed');
    return json.url;
  };

  const startCheckout = useCallback(async () => {
    setRedirecting(true);
    try {
      window.location.href = await callApi('/api/stripe-checkout');
    } catch (e) {
      console.error('checkout error:', e);
      alert('決済ページを開けませんでした。時間をおいて再度お試しください。');
      setRedirecting(false);
    }
  }, []);

  const openPortal = useCallback(async () => {
    setRedirecting(true);
    try {
      window.location.href = await callApi('/api/stripe-portal');
    } catch (e) {
      console.error('portal error:', e);
      alert('お支払い管理を開けませんでした。時間をおいて再度お試しください。');
      setRedirecting(false);
    }
  }, []);

  return (
    <PlanContext.Provider
      value={{
        plan,
        isPro,
        isLifetime,
        limits,
        subscription,
        loading,
        refresh,
        openUpgrade,
        requirePro,
        canAddProject,
        handlePlanError,
        startCheckout,
        openPortal,
        redirecting,
      }}
    >
      {children}
      <UpgradeModal
        open={upgrade.open}
        reason={upgrade.reason}
        loading={redirecting}
        onUpgrade={startCheckout}
        onClose={() => setUpgrade({ open: false, reason: 'default' })}
      />
    </PlanContext.Provider>
  );
}
